import axios from "axios";
import { computed, ComputedRef, reactive } from "vue";

import type { TypedCustomDomain } from "@Resources/js/laravel.d.ts";
import useNotify from "@Composables/useNotify";
import { ParseServerError, ProcessServerErrors } from "@Modules/shared/Api/ServerErrors";
import { HttpRouter } from "@Modules/shared/interfaces/HttpRouter";

export type BrandedDomainCheckResult = {
  active: boolean;
  reason: string | null;
}

export type DomainsApi = {
    domains: ComputedRef<TypedCustomDomain[]>
    loading: ComputedRef<boolean>
    errors: ComputedRef<Record<string, string>>
    fetchOrgDomains: (args: { orgSlug: string }) => Promise<void>
    attachDomainToOrg: (args: { orgSlug: string; domain: string }) => Promise<boolean>
    detachDomainFromOrg: (args: { orgSlug: string; domain: string }) => Promise<boolean>
    attachDomainToTeam: (args: {
        orgSlug: string
        teamSlug: string
        domain: string
    }) => Promise<boolean>
    detachDomainFromTeam: (args: {
        orgSlug: string
        teamSlug: string
        domain: string
    }) => Promise<boolean>
    toggleDomainGlobalStatus: (args: {
        orgSlug: string
        domain: string
        enable: boolean
    }) => Promise<boolean>
    createBrandedDomain: (domain: string, orgSlug: string, isApex?: boolean) => Promise<boolean>
    checkBrandedDomain: (domain: string, orgSlug: string) => Promise<BrandedDomainCheckResult>
    toggleDisableDomain: (domain: string, orgSlug: string) => Promise<boolean>
    setDomainRootRedirect: (domain: string, enabled: boolean, destination: string, orgSlug: string) => Promise<boolean>
    setDomainErrorRedirect: (domain: string, enabled: boolean, destination: string, orgSlug: string) => Promise<boolean>
    setDomainRenewal: (domain: string, enabled: boolean, orgSlug: string) => Promise<boolean>
    deleteDomain: (domain: string, orgSlug: string) => Promise<boolean>
    restoreDomain: (domain: string, orgSlug: string) => Promise<boolean>
    setRouter: (
        router: HttpRouter<"get" | "post" | "patch" | "put" | "delete">
    ) => DomainsApi
    clearDomains: () => void
    clearErrors: () => void
}

/**
 * DomainsApi is used to manage organization domains
 */
export default function DomainsApi(): DomainsApi {
    const state = reactive<{
        domains: TypedCustomDomain[]
        loading: boolean
        errors: Record<string, string>
    }>({
        domains: [],
        loading: false,
        errors: {},
    })

    let router: HttpRouter<"get" | "post" | "put" | "patch" | "delete"> = axios
    const { notifySuccess, notifyError } = useNotify()

    function clearErrors() {
        Object.keys(state.errors).forEach((key) => delete state.errors[key])
    }

    const that: DomainsApi = {
        domains: computed(() => state.domains),
        loading: computed(() => state.loading),
        errors: computed(() => state.errors),
        clearDomains: () => { state.domains = []; },
        clearErrors,

        /**
         * Fetch all domains attached to the organization
         */
        fetchOrgDomains: async function ({ orgSlug }: { orgSlug: string }) {
            clearErrors();
            state.loading = true;
            const url = `/app/api/organization/${orgSlug}/settings/branded-domains`;

            try {
                const response = await router.get(url) as HttpResponse<TypedCustomDomain[]>;

                if (response.status < 200 || response.status >= 300) {
                    throw new Error(`Could not fetch domains for org ${orgSlug}`);
                }

                state.domains = response.data;
            } catch (e) {
                notifyError({ text: ParseServerError(e).serverMessage || "Failed to fetch domains." });
                throw e;
            } finally {
                state.loading = false;
            }
        },

        /**
         * Attach a domain to the organization
         */
        attachDomainToOrg: async function ({
            orgSlug,
            domain,
        }: {
            orgSlug: string
            domain: string
        }): Promise<boolean> {
            clearErrors()
            const url = `/app/api/organization/${orgSlug}/domain/attach`

            try {
                const response = await router.post(url, { domain })

                if (response.status < 200 || response.status >= 300) {
                    throw new Error(`Could not attach domain ${domain}`)
                }

                notifySuccess({ text: "The domain has been attached." })
                return true
            } catch (e) {
                const { fieldErrors, serverMessage } = ParseServerError(e)
                if (fieldErrors) {
                    state.errors = ProcessServerErrors(fieldErrors)
                } else {
                    notifyError({ text: serverMessage || "Failed to attach domain." })
                }
                return false
            }
        },

        /**
         * Detach a domain from the organization
         */
        detachDomainFromOrg: async function ({
            orgSlug,
            domain,
        }: {
            orgSlug: string
            domain: string
        }): Promise<boolean> {
            clearErrors()
            const url = `/app/api/organization/${orgSlug}/domain/detach`

            try {
                const response = await router.post(url, { domain })

                if (response.status < 200 || response.status >= 300) {
                    throw new Error(`Could not detach domain ${domain}`)
                }

                state.domains = state.domains.filter((d) => d.domain !== domain)
                notifySuccess({ text: "The domain has been detached." })
                return true
            } catch (e) {
                const { fieldErrors, serverMessage } = ParseServerError(e)
                if (fieldErrors) {
                    state.errors = ProcessServerErrors(fieldErrors)
                } else {
                    notifyError({ text: serverMessage || "Failed to detach domain." })
                }
                return false
            }
        },

        /**
         * Attach a domain to a specific team
         */
        attachDomainToTeam: async function ({
            orgSlug,
            teamSlug,
            domain,
        }: {
            orgSlug: string
            teamSlug: string
            domain: string
        }): Promise<boolean> {
            clearErrors()
            const url = `/app/api/organization/${orgSlug}/team/${teamSlug}/domain/attach`

            try {
                const response = await router.post(url, { domain })

                if (response.status < 200 || response.status >= 300) {
                    throw new Error(`Could not attach domain ${domain} to team ${teamSlug}`)
                }

                notifySuccess({ text: "The domain has been attached to the team." })
                return true
            } catch (e) {
                const { fieldErrors, serverMessage } = ParseServerError(e)
                if (fieldErrors) {
                    state.errors = ProcessServerErrors(fieldErrors)
                } else {
                    notifyError({
                        text: serverMessage || "Failed to attach domain to team.",
                    })
                }
                return false
            }
        },

        /**
         * Detach a domain from a specific team
         */
        detachDomainFromTeam: async function ({
            orgSlug,
            teamSlug,
            domain,
        }: {
            orgSlug: string
            teamSlug: string
            domain: string
        }): Promise<boolean> {
            clearErrors()
            const url = `/app/api/organization/${orgSlug}/team/${teamSlug}/domain/detach`

            try {
                const response = await router.post(url, { domain })

                if (response.status < 200 || response.status >= 300) {
                    throw new Error(`Could not detach domain ${domain} from team ${teamSlug}`)
                }

                notifySuccess({ text: "The domain has been detached from the team." })
                return true
            } catch (e) {
                const { fieldErrors, serverMessage } = ParseServerError(e)
                if (fieldErrors) {
                    state.errors = ProcessServerErrors(fieldErrors)
                } else {
                    notifyError({
                        text: serverMessage || "Failed to detach domain from team.",
                    })
                }
                return false
            }
        },

        /**
         * Toggle whether a domain is global (for all teams)
         */
        toggleDomainGlobalStatus: async function ({
            orgSlug,
            domain,
            enable,
        }: {
            orgSlug: string
            domain: string
            enable: boolean
        }): Promise<boolean> {
            clearErrors()
            const url = `/app/api/organization/${orgSlug}/domain/status`

            try {
                const response = await router.patch(url, {
                    domain,
                    status: "for_all_teams",
                    enable,
                })

                if (response.status < 200 || response.status >= 300) {
                    throw new Error(`Could not update domain ${domain} status`)
                }

                // Update local state
                const domainToUpdate = state.domains.find((d) => d.domain === domain)
                if (domainToUpdate) {
                    // domainToUpdate.for_all_teams = enable
                }

                notifySuccess({
                    text: `The domain is globally ${enable ? "enabled" : "disabled"}.`,
                })
                return true
            } catch (e) {
                const { fieldErrors, serverMessage } = ParseServerError(e)
                if (fieldErrors) {
                    state.errors = ProcessServerErrors(fieldErrors)
                } else {
                    notifyError({
                        text: serverMessage || "Failed to update domain status.",
                    })
                }
                return false
            }
        },

        /**
         * Create a new branded domain
         */
        createBrandedDomain: async function (domain: string, orgSlug: string, isApex = true): Promise<boolean> {
          clearErrors();
          const url = `/app/api/organization/${orgSlug}/settings/branded-domain`;

          try {
            const response = await router.post(url, { domain, simpleCheck: false, isApexDomain: isApex });

            if (response.status < 200 || response.status >= 300) {
              throw new Error(`Could not create branded domain ${domain}`);
            }

            notifySuccess({ text: "Your new domain has been created." });
            return true
          } catch (e) {
            const { fieldErrors, serverMessage } = ParseServerError(e);
            if (fieldErrors) {
              state.errors = ProcessServerErrors(fieldErrors);
            } else {
              notifyError({ text: serverMessage || "Failed to create domain." });
            }
            return false;
          }
        },

        /**
         * Check if a branded domain is active
         */
        checkBrandedDomain: async function (domain: string, orgSlug: string): Promise<BrandedDomainCheckResult> {
          const url = `/app/api/organization/${orgSlug}/settings/branded-domain/${domain}/check`;

          try {
            const response = await router.post(url) as HttpResponse<BrandedDomainCheckResult>;

            return response.data;
          } catch (e) {
            notifyError({ text: ParseServerError(e).serverMessage || "Failed to check domain status." });
            return { active: false, reason: null };
          }
        },

        /**
         * Toggle the disabled state of a branded domain
         */
        toggleDisableDomain: async function (domain: string, orgSlug: string): Promise<boolean> {
          clearErrors();
          const url = `/app/api/organization/${orgSlug}/settings/branded-domain/${domain}/toggleDisable`;

          try {
            const response = await router.put(url, { domain });

            if (response.status < 200 || response.status >= 300) {
              throw new Error(`Could not toggle disable state for domain ${domain}`);
            }

            notifySuccess({ text: "The domain has been updated." });

            return true;
          } catch (e) {
            notifyError({ text: ParseServerError(e).serverMessage || "Failed to update domain." });
            return false;
          }
        },

        /**
         * Set the root redirect for a branded domain
         */
        setDomainRootRedirect: async function (domain: string, enabled: boolean, destination: string, orgSlug: string): Promise<boolean> {
          clearErrors();
          const url = `/app/api/organization/${orgSlug}/settings/branded-domain/root-redirect`;

          try {
            const response = await router.put(url, { domain, enabled, destination });

            if (response.status < 200 || response.status >= 300) {
              throw new Error(`Could not set root redirect for domain ${domain}`);
            }

            notifySuccess({ text: "The domain has been updated." });

            return true;
          } catch (e) {
            notifyError({ text: ParseServerError(e).serverMessage || "Failed to update domain redirect." });
            return false;
          }
        },

        /**
         * Set the 404 error redirect for a branded domain
         */
        setDomainErrorRedirect: async function (domain: string, enabled: boolean, destination: string, orgSlug: string): Promise<boolean> {
          clearErrors();
          const url = `/app/api/organization/${orgSlug}/settings/branded-domain/error-redirect`;

          try {
            const response = await router.put(url, { domain, enabled, destination });

            if (response.status < 200 || response.status >= 300) {
              throw new Error(`Could not set error redirect for domain ${domain}`);
            }

            notifySuccess({ text: "The domain has been updated." });

            return true;
          } catch (e) {
            notifyError({ text: ParseServerError(e).serverMessage || "Failed to update domain redirect." });
            return false;
          }
        },

        /**
         * Set the renewal state for a branded domain
         */
        setDomainRenewal: async function (domain: string, enabled: boolean, orgSlug: string): Promise<boolean> {
          clearErrors();
          const url = `/app/api/organization/${orgSlug}/settings/branded-domain/renewal`;

          try {
            const response = await router.put(url, { domain, enabled });

            if (response.status < 200 || response.status >= 300) {
              throw new Error(`Could not set renewal for domain ${domain}`);
            }

            notifySuccess({ text: "The domain has been updated." });

            return true;
          } catch (e) {
            notifyError({ text: ParseServerError(e).serverMessage || "Failed to update domain renewal." });
            return false;
          }
        },

        /**
         * Delete a branded domain
         */
        deleteDomain: async function (domain: string, orgSlug: string): Promise<boolean> {
          clearErrors();
          const url = `/app/api/organization/${orgSlug}/settings/branded-domain/${domain}`;

          try {
            const response = await router.delete(url);

            if (response.status < 200 || response.status >= 300) {
              throw new Error(`Could not delete domain ${domain}`);
            }

            notifySuccess({ text: "The domain has been deleted." });

            return true;
          } catch (e) {
            notifyError({ text: ParseServerError(e).serverMessage || "Failed to delete domain." });
            return false;
          }
        },

        /**
         * Restore a deleted branded domain
         */
        restoreDomain: async function (domain: string, orgSlug: string): Promise<boolean> {
          clearErrors();
          const url = `/app/api/organization/${orgSlug}/settings/branded-domain/${domain}/restore`;

          try {
            const response = await router.patch(url);

            if (response.status < 200 || response.status >= 300) {
              throw new Error(`Could not restore domain ${domain}`);
            }

            notifySuccess({ text: "The domain has been restored." });

            return true;
          } catch (e) {
            notifyError({ text: ParseServerError(e).serverMessage || "Failed to restore domain." });
            return false;
          }
        },

        /**
         * Allows you to swap out the router for a fake one when writing tests
         */
        setRouter(newRouter: HttpRouter<"get" | "post" | "put" | "patch" | "delete">) {
            router = newRouter
            return that
        },
    }

    return that
}
